import { Fragment, useCallback, useEffect, useState } from "react";
import { Button, Checkbox, Flex, Icon, Modal } from "@repo/ping-design";

import { ColumnNode, groupPathKey, isInGroup, isSameNode, moveColumnNode } from "./utils";

import "./TopBuildingsListConfigureModal.scss";

export type ColumnConfigItem = {
  key: string;
  name: string;
  isVisible: boolean;
  // Nested groups, outermost first; see ConfigurableColumn.groups.
  groups?: string[];
};

// Group header rows that start before `index`: one per level where its path leaves the previous item's.
const groupHeadersBefore = (items: ColumnConfigItem[], index: number): string[][] => {
  const path = items[index].groups ?? [];
  const previous = index > 0 ? (items[index - 1].groups ?? []) : [];
  const shared = path.findIndex((name, depth) => previous[depth] !== name);
  if (shared === -1) return [];
  return path.slice(shared).map((_, offset) => path.slice(0, shared + offset + 1));
};

const ROW_SELECTOR = ".TopBuildingsListConfigureModal__ColumnItem, .TopBuildingsListConfigureModal__GroupItem";

type TopBuildingsListConfigureModalProps = {
  isOpen: boolean;
  onClose: () => void;
  items: ColumnConfigItem[];
  isCustomized: boolean;
  onSave: (items: ColumnConfigItem[]) => void;
  onReset: () => void;
};

export const TopBuildingsListConfigureModal = ({
  isOpen,
  onClose,
  items,
  isCustomized,
  onSave,
  onReset,
}: TopBuildingsListConfigureModalProps) => {
  const [localItems, setLocalItems] = useState<ColumnConfigItem[]>(items);
  const [draggedNode, setDraggedNode] = useState<ColumnNode | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<ReadonlySet<string>>(() => new Set());

  const isExpanded = (path: string[]) => !collapsedGroups.has(groupPathKey(path));
  const isShown = (path: string[], depth: number) =>
    path.slice(0, depth).every((_, i) => isExpanded(path.slice(0, i + 1)));

  const handleToggleExpanded = useCallback((path: string[]) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      const key = groupPathKey(path);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }, []);

  useEffect(() => {
    setLocalItems(items);
  }, [items]);

  const handleToggle = useCallback((key: string) => {
    setLocalItems((prev) => prev.map((item) => (item.key === key ? { ...item, isVisible: !item.isVisible } : item)));
  }, []);

  // Shows the whole group unless all of it is already visible, in which case it hides it.
  const handleToggleGroup = useCallback((path: string[]) => {
    setLocalItems((prev) => {
      const show = prev.some((item) => isInGroup(item, path) && !item.isVisible);
      return prev.map((item) => (isInGroup(item, path) ? { ...item, isVisible: show } : item));
    });
  }, []);

  const handleDragStart = useCallback((e: React.DragEvent, node: ColumnNode) => {
    setDraggedNode(node);
    e.dataTransfer.effectAllowed = "move";
    const row = e.currentTarget.closest(ROW_SELECTOR);
    if (row) {
      const { left, top } = row.getBoundingClientRect();
      e.dataTransfer.setDragImage(row, e.clientX - left, e.clientY - top);
    }
  }, []);

  const handleDragOver = useCallback(
    (e: React.DragEvent, start: number, end: number = start) => {
      e.preventDefault();
      if (draggedNode === null) return;
      setLocalItems((prev) => moveColumnNode(prev, draggedNode, start, end));
    },
    [draggedNode],
  );

  const handleDragEnd = useCallback(() => {
    setDraggedNode(null);
  }, []);

  const isDragging = (item: ColumnConfigItem) =>
    draggedNode !== null &&
    ("key" in draggedNode ? draggedNode.key === item.key : isInGroup(item, draggedNode.groupPath));

  const handleSave = useCallback(() => {
    onSave(localItems);
  }, [onSave, localItems]);

  const handleCancel = useCallback(() => {
    setTimeout(() => setLocalItems(items), 0);
    onClose();
  }, [onClose, items]);

  return (
    <Modal
      title="Configure Columns"
      isOpen={isOpen}
      onClose={handleCancel}
      className="TopBuildingsListConfigureModal"
      renderContent={() => (
        <div className="TopBuildingsListConfigureModal__Content">
          <div className="TopBuildingsListConfigureModal__Content__Header">
            <div>Column Name</div>
            <div>Visibility</div>
          </div>
          {localItems.map((item, index) => (
            <Fragment key={item.key}>
              {groupHeadersBefore(localItems, index).map((path) => {
                if (!isShown(path, path.length - 1)) return null;
                const members = localItems.filter((member) => isInGroup(member, path));
                const visible = members.filter((member) => member.isVisible).length;
                const node = { groupPath: path };
                const expanded = isExpanded(path);
                return (
                  <div
                    key={path.join("/")}
                    className={`TopBuildingsListConfigureModal__GroupItem${
                      isSameNode(draggedNode, node) ? " TopBuildingsListConfigureModal__GroupItem--Dragging" : ""
                    }`}
                    style={{ paddingLeft: `calc(var(--ping-spacing-0-5) + ${(path.length - 1) * 1.5}rem)` }}
                    onDragOver={(e) => handleDragOver(e, index, expanded ? index : index + members.length - 1)}
                  >
                    <div className="TopBuildingsListConfigureModal__GroupItem__Label">
                      <button
                        type="button"
                        className="TopBuildingsListConfigureModal__ExpandToggle"
                        aria-label={`${expanded ? "Collapse" : "Expand"} ${path[path.length - 1]}`}
                        aria-expanded={expanded}
                        onClick={() => handleToggleExpanded(path)}
                      >
                        <Icon iconName={expanded ? "ChevronUp" : "ChevronDown"} size={16} />
                      </button>
                      <div
                        className="TopBuildingsListConfigureModal__GroupItem__Label__Draggable"
                        draggable
                        onDragStart={(e) => handleDragStart(e, node)}
                        onDragEnd={handleDragEnd}
                      >
                        <div className="TopBuildingsListConfigureModal__DragHandle">
                          <Icon iconName="GripVertical" size={16} />
                        </div>
                        <span className="TopBuildingsListConfigureModal__GroupItem__Label__Draggable__Text">
                          {path[path.length - 1]}
                        </span>
                      </div>
                    </div>
                    <Checkbox
                      name={`group:${path.join("/")}`}
                      checked={visible === members.length}
                      indeterminate={visible > 0 && visible < members.length}
                      onChange={() => handleToggleGroup(path)}
                    />
                  </div>
                );
              })}
              {isShown(item.groups ?? [], item.groups?.length ?? 0) && (
                <div
                  className={`TopBuildingsListConfigureModal__ColumnItem${
                    isDragging(item) ? " TopBuildingsListConfigureModal__ColumnItem--Dragging" : ""
                  }`}
                  style={
                    item.groups?.length
                      ? { paddingLeft: `calc(var(--ping-spacing-0-5) + ${item.groups.length * 1.5}rem)` }
                      : undefined
                  }
                  onDragOver={(e) => handleDragOver(e, index)}
                >
                  <div
                    className="TopBuildingsListConfigureModal__ColumnItem__Label"
                    draggable
                    onDragStart={(e) => handleDragStart(e, { key: item.key })}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="TopBuildingsListConfigureModal__ExpandSlot" />
                    <div className="TopBuildingsListConfigureModal__DragHandle">
                      <Icon iconName="GripVertical" size={16} />
                    </div>
                    <div className="TopBuildingsListConfigureModal__ColumnItem__Label__LabelText">{item.name}</div>
                  </div>
                  <Checkbox name={item.key} checked={item.isVisible} onChange={() => handleToggle(item.key)} />
                </div>
              )}
            </Fragment>
          ))}
        </div>
      )}
      renderFooter={() => (
        <Flex gap="2" justify="space-between" className="TopBuildingsListConfigureModal__Footer">
          <Button label="Reset Defaults" variant="outline" disabled={!isCustomized} onClick={onReset} />
          <Flex gap="2">
            <Button label="Cancel" variant="ghost" onClick={handleCancel} />
            <Button label="Save" onClick={handleSave} />
          </Flex>
        </Flex>
      )}
    />
  );
};
